import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';

import Skeleton from '../../../../../components/Skeleton';
import Tooltip from '../../../../../components/Tooltip';
import useHeatmapConfig from '../../../hooks/useHeatmapConfig';
import { format, endOfMonth, isSame } from '../../../../../utils/date';
import { initMaxDate } from '../../../configs/chart-config';
import { formatValue } from '../../../utils/heatmap';

const ShareHeatmapTable = ({ months, data }) => {
  const { yearGroups, lastMonthIndex, getBackground } = useHeatmapConfig({ months, data });

  return (
    <div className="share-heatmap-container">
      <table className="ai-dashboard-heatmap share-heatmap">
        <thead>
          <tr className="ai-dashboard-heatmap__year-row">
            <th />
            {yearGroups.map(({ year, count }) => (
              <th key={year} colSpan={count}>
                {year}
              </th>
            ))}
          </tr>
          <tr className="ai-dashboard-heatmap__month-row">
            <th>
              <div className="d-flex align-items-center">App</div>
            </th>
            {months.map((date, i) => (
              <th key={date}>
                {i === lastMonthIndex && !isSame(initMaxDate, endOfMonth(date)) ? (
                  <div className="d-flex align-items-center">
                    <span>{format(date, 'MMM')} (partial)</span>{' '}
                    <Tooltip content="Partial month" placement="top" colorVariant="light">
                      <span className="hicon-question-circle" />
                    </Tooltip>
                  </div>
                ) : (
                  format(date, 'MMM')
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(({ app, values }) => (
            <tr key={app.unified_ref_no}>
              <td className="share-heatmap__app-cell text-truncate">{app.nickname || app.name}</td>
              {values.map((value, i) => {
                const isPartialCell =
                  i === values.length - 1 && !isSame(initMaxDate, endOfMonth(months[i]));
                return (
                  <td
                    key={months[i]}
                    style={{ backgroundColor: getBackground(value) }}
                    className={cx({ 'is-partial': isPartialCell })}
                  >
                    {formatValue(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MAX_DISPLAYED_MONTHS = 8;

const ShareHeatmap = ({ metricSeries, apps, isPending }) => {
  const { months, data } = useMemo(() => {
    const rows = apps.map((app) => {
      const fullSeries = metricSeries[app.unified_ref_no] || [];
      const sliceStartIndex = Math.max(fullSeries.length - MAX_DISPLAYED_MONTHS, 0);
      return {
        app,
        series: fullSeries.slice(sliceStartIndex),
      };
    });

    const isAnyRowDataPresent = rows.some(({ series }) => !isEmpty(series));

    if (!isAnyRowDataPresent) return { months: [], data: [] };

    const firstNonEmptyRow = rows.find(({ series }) => !isEmpty(series));

    const paddedRows = rows.map((row) => {
      if (isEmpty(row.series)) {
        return {
          app: row.app,
          series: firstNonEmptyRow.series.map(([ts]) => [ts, null]),
        };
      }

      return row;
    });

    const derivedMonths = firstNonEmptyRow.series.map(([ts]) => format(ts));

    return {
      months: derivedMonths,
      data: paddedRows.map(({ app, series }) => ({
        app,
        values: series.map(([, value]) => value),
      })),
    };
  }, [metricSeries, apps]);

  if (isPending) {
    return (
      <div className="d-flex p-mdl b-t">
        <Skeleton height={496} />
      </div>
    );
  }

  if (isEmpty(months) || isEmpty(data)) {
    return (
      <div className="d-flex p-mdl b-t">
        <div className="ai-dashboard-heatmap__empty flex-grow">No data available</div>
      </div>
    );
  }

  return <ShareHeatmapTable months={months} data={data} />;
};

const AppPropType = T.shape({
  unified_ref_no: T.string,
  nickname: T.string,
  name: T.string,
});

ShareHeatmapTable.propTypes = {
  months: T.arrayOf(T.string),
  data: T.arrayOf(
    T.shape({
      app: AppPropType,
      values: T.arrayOf(T.number),
    })
  ),
};

ShareHeatmap.propTypes = {
  metricSeries: T.object.isRequired,
  apps: T.array.isRequired,
  isPending: T.bool.isRequired,
};

export default ShareHeatmap;
